import type { DatasetFormEditorProps } from "./dataset-form-editor.type";

import { Form } from "antd";
import isEqual from "lodash/isEqual";

import {
  type DatasetFormDataJson,
  type DatasetFormCreateVariables,
  type DatasetFormUpdateVariables,
  DATASET_TYPE,
} from "@/entities/dataset";
import { getDatasetFormData } from "@/modules/dataset-form";
import { useDatasetsMutatorSync } from "@/modules/datasets/datasets-mutator";

const datasetType = DATASET_TYPE.FORM;

export const useDatasetFormEditor = ({
  template,
  dataset,
}: DatasetFormEditorProps) => {
  const [form] = Form.useForm();
  const initialValues = getDatasetFormData(dataset) || {};

  const schema = template.form;

  const { addMutatorItem, removeMutatorItem } = useDatasetsMutatorSync();

  const onFormChange = (values: Record<string, unknown>) => {
    const newData: DatasetFormDataJson = values;

    const cleanedValues = Object.fromEntries(
      Object.entries(newData).filter(
        ([_, value]) =>
          value !== null &&
          value !== undefined &&
          (typeof value !== "string" || value.trim() !== "")
      )
    );
    const hasValues = Object.keys(cleanedValues).length > 0;

    if (dataset) {
      const datasetId = dataset.id;
      const existingData = getDatasetFormData(dataset);
      const noChanges = isEqual(cleanedValues, existingData);

      if (noChanges) {
        removeMutatorItem({ datasetId, datasetType });
        return;
      }

      if (!hasValues) {
        addMutatorItem({ mutationType: "delete", datasetType, datasetId });
        return;
      }

      const mutationType = "update";
      const variables: DatasetFormUpdateVariables = { data: cleanedValues };
      addMutatorItem({ mutationType, datasetType, datasetId, variables });
      return;
    }

    if (!hasValues) {
      removeMutatorItem({ datasetType });
      return;
    }

    const variables: DatasetFormCreateVariables = {
      type: datasetType,
      name: template.name,
      source: schema ?? undefined,
      data: cleanedValues,
      layout_index: template.layout_index,
    };

    addMutatorItem({ mutationType: "create", datasetType, variables });
  };

  return {
    form,
    schema,
    initialValues,
    onFormChange,
  };
};
